import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Device } from "@twilio/voice-sdk";
import { useDispatch, useSelector } from "react-redux";
import { callOutboundPreauthRequest } from "../../store/Twilio/action";
import {
  MIC_PERMISSION,
  useTwilioAudioDevices,
} from "./useTwilioAudioDevices";

const TwilioCallContext = createContext(null);

const AUTO_ACCEPT_INBOUND_FOR_TESTING = false;

const CALL_PHASE = {
  IDLE: "idle",
  GETTING_TOKEN: "getting_token",
  REGISTERING: "registering",
  READY: "ready",
  DIALLING: "dialling",
  INCOMING: "incoming",
  CONNECTED: "connected",
  ENDED: "ended",
  FAILED: "failed",
};

const getAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser"));
  } catch {
    return null;
  }
};

const getTwilioClientIdentity = () => {
  const authUser = getAuthUser();

  return String(
    authUser?.get_user_data?.User_IdExternal ||
      authUser?.get_user_data?.User_Id ||
      "agent_1"
  );
};

const getCanUseTwilio = () => {
  const authUser = getAuthUser();
  const user = authUser?.get_user_data || {};

  return (
    user?.User_FlagDeveloper === "1" ||
    user?.User_FlagTwilio === "1" ||
    user?.User_FlagCallAgent === "1"
  );
};

const getCompanyExternalId = () => {
  const authUser = getAuthUser();

  return (
    authUser?.get_company_data?.Company_IdExternal ||
    authUser?.get_company_data?.Company_Id ||
    null
  );
};

const normalisePhone = (value = "") => {
  const cleaned = String(value)
    .replace(/[^\d+]/g, "")
    .replace(/(?!^)\+/g, "");

  if (!cleaned) return "";
  if (cleaned.startsWith("+")) return cleaned;
  if (cleaned.startsWith("00")) return `+${cleaned.slice(2)}`;
  if (cleaned.startsWith("0")) return `+44${cleaned.slice(1)}`;

  return cleaned;
};

const getTokenFromResponse = (preauthData) =>
  preauthData?.Token ||
  preauthData?.token ||
  preauthData?.AccessToken ||
  preauthData?.accessToken ||
  "";

// The backend mints Twilio Network Traversal Service credentials and sends them
// with the voice token. This matters more than it looks: @twilio/voice-sdk ships
// NO ice servers of its own (grep the package - "iceServers" appears only in the
// preflight typings), so without these the browser gathers host candidates and
// nothing else. Twilio's media gateway is ice-lite, meaning it never sends
// connectivity checks and only ever responds, so a browser behind NAT with no
// STUN and no TURN has no route to it at all.
const getIceServersFromResponse = (preauthData) => {
  const servers = preauthData?.IceServers || preauthData?.iceServers;

  if (!Array.isArray(servers)) return [];

  return servers.filter((server) => server?.urls);
};

// Sticky across reloads: once a network has proved it cannot carry media any
// other way, there is no point making the user rediscover that every session.
const FORCE_TURN_RELAY_STORAGE_KEY = "twilioForceTurnRelay";

const readForceTurnRelay = () => {
  try {
    return localStorage.getItem(FORCE_TURN_RELAY_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const writeForceTurnRelay = (forceRelay) => {
  try {
    if (forceRelay) {
      localStorage.setItem(FORCE_TURN_RELAY_STORAGE_KEY, "1");
    } else {
      localStorage.removeItem(FORCE_TURN_RELAY_STORAGE_KEY);
    }
  } catch (error) {
    console.warn("Could not persist the TURN relay preference:", error);
  }
};

// Twilio signalling errors that mean "the websocket went away", not "this user
// broke something". They fire on token expiry, laptop sleep, wifi drops and
// proxy timeouts, so they are recoverable by getting a fresh token and
// re-registering rather than parking the device in a permanent failed state.
// 31009 TransportError, 31204/31205/31207 JWT problems.
//
// Two codes are deliberately NOT here:
//   31005 — on this account it is the TwiML 13214 caller-ID failure.
//   31206 — RateExceededError. Reconnecting in response to a rate limit is the
//           one thing guaranteed to make it worse, so it backs off instead.
const RECOVERABLE_SIGNALLING_ERROR_CODES = [31009, 31204, 31205, 31207];

// Twilio dropped us for sending too much, too fast. Everything after this on the
// same socket comes back as 31009, so the only sane response is to wait.
const RATE_LIMITED_ERROR_CODE = 31206;
const RATE_LIMIT_BACKOFF_MS = 60000;

// MediaErrors.ConnectionError — signalling is fine, the audio path is not. The
// browser could not build a working ICE/DTLS connection to Twilio's media
// gateway. Retrying the signalling side cannot fix it.
const MEDIA_CONNECTION_ERROR_CODE = 53405;

// Wait between reconnect attempts so a genuinely dead connection does not spin.
const RECONNECT_RETRY_MS = 15000;
const RECONNECT_MAX_ATTEMPTS = 5;

// Ask for the next token a minute before the current one dies. The SDK default
// is 10s, which is not enough headroom for the token round trip on a slow link.
const TOKEN_REFRESH_MS = 60000;

const isRecoverableSignallingError = (error) =>
  RECOVERABLE_SIGNALLING_ERROR_CODES.includes(Number(error?.code));

// device.connect() throws this when the SDK already has a live call. The call
// it is complaining about is fine — it is our second dial that is wrong — so
// this must never be treated as "the call failed".
const isCallAlreadyActiveError = (error) =>
  error?.name === "InvalidStateError" ||
  String(error?.message || "")
    .toLowerCase()
    .includes("call is already active");

// The device knows whether it is on a call even when our own refs have drifted.
const getDeviceIsBusy = (device) => {
  if (!device || device.state === "destroyed") return false;

  try {
    return !!device.isBusy || (device.calls?.length || 0) > 0;
  } catch {
    return false;
  }
};

// Plain-English text for the failures a caller can actually act on.
const getCallErrorMessage = (error) => {
  const code = Number(error?.code);

  if (code === MEDIA_CONNECTION_ERROR_CODE) {
    return "Call failed: no audio path to Twilio (media/ICE blocked).";
  }

  if (code === RATE_LIMITED_ERROR_CODE) {
    return "Call failed: Twilio rate limit hit.";
  }

  if (RECOVERABLE_SIGNALLING_ERROR_CODES.includes(code)) {
    return "Call dropped: lost connection to Twilio.";
  }

  return getErrorMessage(error, "Call failed");
};

const getErrorMessage = (error, fallback = "Something went wrong") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  return (
    error?.message ||
    error?.description ||
    error?.error ||
    error?.Message ||
    fallback
  );
};

const mapToObject = (mapLike) => {
  if (!mapLike || typeof mapLike.forEach !== "function") return {};

  const result = {};

  mapLike.forEach((value, key) => {
    result[key] = value;
  });

  return result;
};

const safeJsonParse = (value, fallback = null) => {
  try {
    if (!value || typeof value !== "string") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const parseTwilioParamsString = (paramsString = "") => {
  if (!paramsString || typeof paramsString !== "string") return {};

  try {
    const searchParams = new URLSearchParams(paramsString);
    const result = {};

    searchParams.forEach((value, key) => {
      result[key] = value;
    });

    return result;
  } catch (error) {
    console.error("Failed to parse Twilio Params string:", error);
    return {};
  }
};

const buildDisplayNameFromCustomerData = (customerData = {}) => {
  const parts = [
    customerData.FirstName || customerData.firstName || customerData.first_name,
    customerData.LastName || customerData.lastName || customerData.last_name,
  ].filter(Boolean);

  if (parts.length) return parts.join(" ");

  return (
    customerData.FullName ||
    customerData.fullName ||
    customerData.Name ||
    customerData.name ||
    ""
  );
};

const getIncomingCaller = (call) => {
  const params = call?.parameters || {};

  return (
    params.From ||
    params.Caller ||
    params.CallerId ||
    params.CallSid ||
    "Unknown caller"
  );
};

const getIncomingMeta = (call) => {
  const customParameters = call?.customParameters;
  const parameters = call?.parameters || {};
  const parsedParams = parseTwilioParamsString(parameters.Params || "");

  const customerData =
    safeJsonParse(parsedParams.customer_data, {}) ||
    safeJsonParse(parsedParams.customerData, {}) ||
    {};

  const getCustomParam = (key) => {
    try {
      return customParameters?.get?.(key) || "";
    } catch {
      return "";
    }
  };

  const customerNameFromData = buildDisplayNameFromCustomerData(customerData);

  return {
    customerName:
      getCustomParam("customerName") ||
      getCustomParam("CustomerName") ||
      parsedParams.customerName ||
      parsedParams.CustomerName ||
      customerNameFromData ||
      "",

    customerId:
      getCustomParam("customerId") ||
      getCustomParam("CustomerId") ||
      parsedParams.customerId ||
      parsedParams.CustomerId ||
      customerData.Id ||
      customerData.id ||
      "",

    leadId:
      getCustomParam("leadId") ||
      getCustomParam("LeadId") ||
      parsedParams.leadId ||
      parsedParams.LeadId ||
      "",

    source:
      getCustomParam("source") ||
      getCustomParam("Source") ||
      parsedParams.source ||
      parsedParams.Source ||
      "",

    phone:
      getCustomParam("phone") ||
      getCustomParam("Phone") ||
      parsedParams.phone ||
      parsedParams.Phone ||
      parameters.From ||
      parameters.Caller ||
      "",

    from: parameters.From || "",
    to: parameters.To || "",
    callSid: parameters.CallSid || "",
    accountSid: parameters.AccountSid || "",

    customerData,
    parsedParams,
    rawCustomParameters: mapToObject(customParameters),
    rawParameters: parameters,
  };
};

const buildOutboundMeta = ({ phone, person }) => ({
  customerName: person?.name || "",
  // A contact person carries its own id AND the customer it belongs to, so the
  // "Open Customer" route still works when you dial the contact rather than the
  // account.
  customerId:
    person?.customerId || (person?.type === "Customer" ? person?.id || "" : ""),
  contactPersonId: person?.type === "ContactPerson" ? person?.id || "" : "",
  leadId: person?.type === "Lead" ? person?.id || "" : "",
  source: person?.type || "outbound",
  phone,
  from: "",
  to: phone,
  callSid: "",
  accountSid: "",
  customerData: person || {},
  parsedParams: {},
  rawCustomParameters: {},
  rawParameters: {},
});

export const TwilioCallProvider = ({ children }) => {
  const dispatch = useDispatch();

  const deviceRef = useRef(null);
  const activeCallRef = useRef(null);
  const incomingCallRef = useRef(null);
  const pendingOutboundPhoneRef = useRef("");
  const pendingOutboundMetaRef = useRef({});
  const lastTokenRef = useRef("");
  const hasRequestedRegisterTokenRef = useRef(false);
  const isRefreshingTokenRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const isDiallingRef = useRef(false);
  const isRateLimitedRef = useRef(false);
  // ICE servers from the last token response, and whether this machine has to
  // stay on TURN relay. Held in refs because both are read inside SDK callbacks
  // and at dial time, where a stale closure would silently drop them.
  const iceServersRef = useRef([]);
  const forceTurnRelayRef = useRef(readForceTurnRelay());
  // Mirrors connectedAt so the disconnect handler can tell an answered call from
  // one that only ever rang, without closing over stale state.
  const connectedAtRef = useRef(null);

  const identity = useMemo(() => getTwilioClientIdentity(), []);
  const companyId = useMemo(() => getCompanyExternalId(), []);
  const canUseTwilio = useMemo(() => getCanUseTwilio(), []);

  const [callPhase, setCallPhase] = useState(CALL_PHASE.IDLE);
  const [callStatus, setCallStatus] = useState("Idle");
  const [incomingCaller, setIncomingCaller] = useState("");
  const [incomingMeta, setIncomingMeta] = useState({});
  const [lastError, setLastError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  const [durationSeconds, setDurationSeconds] = useState(0);
  // Tracks the SDK's own view of "on a call", so the dialler stays locked and
  // End stays available even if our refs lose the call.
  const [isDeviceBusy, setIsDeviceBusy] = useState(false);
  // Surfaced so the phone panel can show that this machine is pinned to relay,
  // and offer a way back off it once the network is fixed.
  const [isForcingTurnRelay, setIsForcingTurnRelay] = useState(
    forceTurnRelayRef.current
  );
  // Microphone permission and mic/speaker/ringtone selection. Everything it
  // exposes is spread onto the context below, so the Phone panel gets it
  // without having to reach for a second hook.
  const audioDevices = useTwilioAudioDevices();
  const {
    bindDevice: bindAudioDevice,
    applyInputPreference,
    micPermission,
  } = audioDevices;

  const {
    loading: tokenLoading = false,
    preauthData = null,
    error: tokenError = null,
  } = useSelector((state) => state.twilioReducer || {});

  const clearIncomingUi = useCallback(() => {
    setIncomingCaller("");
    setIncomingMeta({});
  }, []);

  const resetLiveCallState = useCallback(() => {
    isDiallingRef.current = false;
    setIsMuted(false);
    setIsOnHold(false);
    connectedAtRef.current = null;
    setConnectedAt(null);
    setDurationSeconds(0);
    setIsDeviceBusy(false);
  }, []);

  const syncDeviceBusy = useCallback(() => {
    setIsDeviceBusy(getDeviceIsBusy(deviceRef.current));
  }, []);

  /**
   * The RTCPeerConnection config handed to every call, outbound and inbound.
   *
   * iceTransportPolicy "relay" throws away host and server-reflexive candidates
   * and uses TURN only. That is the right answer on a network that blocks
   * outbound UDP, because Twilio's NTS list includes
   * turn:global.turn.twilio.com:443?transport=tcp, which looks like ordinary
   * HTTPS to a firewall. It is the wrong default everywhere else: it forces all
   * media through a relay, adding a hop and latency for people whose network is
   * fine. So it is opt-in, set automatically the first time a call dies of a
   * media failure on this machine.
   */
  const buildRtcConfiguration = useCallback(() => {
    const iceServers = iceServersRef.current;

    if (!iceServers.length) return undefined;

    return {
      iceServers,
      iceTransportPolicy: forceTurnRelayRef.current ? "relay" : "all",
    };
  }, []);

  const setForceTurnRelay = useCallback((forceRelay) => {
    const next = !!forceRelay;

    forceTurnRelayRef.current = next;
    setIsForcingTurnRelay(next);
    writeForceTurnRelay(next);
    setCallStatus(
      next
        ? "Calls will now use a TURN relay over TCP, which gets through a UDP block."
        : "Calls will use the direct media path again."
    );
  }, []);

  const requestToken = useCallback(
    ({ reason, phone = "" }) => {
      if (!canUseTwilio || !identity) return;

      console.log("Requesting Twilio token:", {
        reason,
        phone,
        identity,
      });

      // A refresh feeds an existing Device via updateToken instead of building
      // a new one, so flag it before the response lands.
      isRefreshingTokenRef.current = reason === "refresh";

      lastTokenRef.current = "";

      dispatch(
        callOutboundPreauthRequest({
          To: phone || null,
          Identity: identity,
          ClientIdentity: identity,
          Company_IdExternal: companyId,
          Source: "twilio_global_provider",
          Reason: reason,
        })
      );
    },
    [canUseTwilio, companyId, dispatch, identity]
  );

  const cleanupActiveCalls = useCallback(() => {
    if (incomingCallRef.current) {
      try {
        const status = incomingCallRef.current.status?.();

        if (status === "pending" || status === "ringing") {
          incomingCallRef.current.reject();
        } else {
          incomingCallRef.current.disconnect();
        }
      } catch (error) {
        console.error("Failed to cleanup incoming Twilio call:", error);
      }

      incomingCallRef.current = null;
    }

    if (activeCallRef.current) {
      try {
        activeCallRef.current.disconnect();
      } catch (error) {
        console.error("Failed to disconnect active Twilio call:", error);
      }

      activeCallRef.current = null;
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // Pull a fresh token so the token-arrival effect can updateToken the existing
  // device (or rebuild it if Twilio already destroyed it).
  const scheduleReconnect = useCallback(
    ({ immediate = false } = {}) => {
      if (!canUseTwilio || !identity) return false;
      if (reconnectTimerRef.current) return true;

      if (reconnectAttemptsRef.current >= RECONNECT_MAX_ATTEMPTS) {
        return false;
      }

      reconnectAttemptsRef.current += 1;

      const runReconnect = () => {
        reconnectTimerRef.current = null;
        requestToken({ reason: "refresh", phone: "" });
      };

      if (immediate) {
        runReconnect();
        return true;
      }

      reconnectTimerRef.current = setTimeout(runReconnect, RECONNECT_RETRY_MS);

      return true;
    },
    [canUseTwilio, identity, requestToken]
  );

  const destroyDevice = useCallback(() => {
    clearReconnectTimer();
    isRateLimitedRef.current = false;
    cleanupActiveCalls();

    if (deviceRef.current) {
      try {
        deviceRef.current.disconnectAll();
        deviceRef.current.destroy();
      } catch (error) {
        console.error("Failed to destroy Twilio device:", error);
      }

      deviceRef.current = null;
    }

    resetLiveCallState();
  }, [cleanupActiveCalls, clearReconnectTimer, resetLiveCallState]);

  const bindCallEvents = useCallback(
    (call, direction = "outbound", callMeta = {}) => {
      console.log("Twilio call created:", {
        direction,
        status: call?.status?.(),
        callDirection: call?.direction,
        parameters: call?.parameters,
        customParameters: call?.customParameters,
        callMeta,
      });

      call.on("ringing", () => {
        if (direction === "inbound") {
          setCallPhase(CALL_PHASE.INCOMING);
          setCallStatus("Incoming call ringing...");
          return;
        }

        setCallPhase(CALL_PHASE.DIALLING);
        setCallStatus("Ringing...");
      });

      call.on("accept", () => {
        activeCallRef.current = call;
        incomingCallRef.current = null;
        setIsDeviceBusy(true);

        const label =
          callMeta?.customerName ||
          callMeta?.phone ||
          incomingCaller ||
          "caller";

        setCallPhase(CALL_PHASE.CONNECTED);
        connectedAtRef.current = Date.now();
        setConnectedAt(Date.now());
        setDurationSeconds(0);
        setIsMuted(false);
        setIsOnHold(false);

        setCallStatus(
          direction === "inbound"
            ? `Inbound call connected with ${label}`
            : `Outbound call connected${label ? ` with ${label}` : ""}`
        );
      });

      call.on("disconnect", (disconnectedCall) => {
        console.log("Twilio call disconnected:", {
          direction,
          status: disconnectedCall?.status?.(),
          parameters: disconnectedCall?.parameters,
        });

        // Did this call ever actually connect? Twilio's disconnect event carries
        // no reason, so "answered then hung up" and "rang out / rejected / bad
        // caller ID" both land here — and both used to read "Call ended. Browser
        // phone ready.", which made a failed call look like a normal one.
        const wasAnswered = !!connectedAtRef.current;

        activeCallRef.current = null;
        incomingCallRef.current = null;
        pendingOutboundPhoneRef.current = "";
        pendingOutboundMetaRef.current = {};

        clearIncomingUi();
        resetLiveCallState();

        setCallPhase((prevPhase) => {
          if (prevPhase === CALL_PHASE.FAILED) return prevPhase;
          return deviceRef.current ? CALL_PHASE.READY : CALL_PHASE.ENDED;
        });

        setCallStatus((prevStatus) => {
          if (String(prevStatus).toLowerCase().includes("failed")) {
            return prevStatus;
          }

          if (!wasAnswered && direction === "outbound") {
            return "Call ended without being answered (no answer, rejected, or blocked by the network).";
          }

          return deviceRef.current
            ? "Call ended. Browser phone ready."
            : "Call ended";
        });
      });

      call.on("cancel", () => {
        activeCallRef.current = null;
        incomingCallRef.current = null;
        pendingOutboundPhoneRef.current = "";
        pendingOutboundMetaRef.current = {};

        clearIncomingUi();
        resetLiveCallState();

        setCallPhase(deviceRef.current ? CALL_PHASE.READY : CALL_PHASE.ENDED);
        setCallStatus("Call cancelled. Browser phone ready.");
      });

      call.on("reject", () => {
        activeCallRef.current = null;
        incomingCallRef.current = null;
        pendingOutboundPhoneRef.current = "";
        pendingOutboundMetaRef.current = {};

        clearIncomingUi();
        resetLiveCallState();

        if (direction === "inbound") {
          setCallPhase(CALL_PHASE.READY);
          setCallStatus("Incoming call rejected. Browser phone ready.");
          return;
        }

        setCallPhase(CALL_PHASE.FAILED);
        setCallStatus("Call rejected");
      });

      call.on("error", (error) => {
        console.error("Twilio call error:", error);

        activeCallRef.current = null;
        incomingCallRef.current = null;
        pendingOutboundPhoneRef.current = "";
        pendingOutboundMetaRef.current = {};

        clearIncomingUi();
        resetLiveCallState();

        setLastError(error);
        setCallPhase(CALL_PHASE.FAILED);
        setCallStatus(getCallErrorMessage(error));
      });

      // Media (ICE/DTLS) failed while signalling stayed up — the call has no
      // audio path.
      //
      // Left to itself the SDK answers this by restarting ICE on a tight loop:
      // ~40 reinvites in five seconds, which Twilio answers with 31206 Rate
      // limit exceeded, closes the websocket, and then every later message
      // raises 31009 — dozens of them — until "Exceeded max ICE retries", by
      // which point the device is unregistered and the browser phone is dead
      // until it re-registers. One unusable call becomes a broken session.
      //
      // When the browser gathered NO candidates at all, no amount of restarting
      // will produce one, so hang up immediately and fail cleanly instead.
      call.on("reconnecting", (error) => {
        console.warn("Twilio call reconnecting:", error);

        setLastError(error);

        if (Number(error?.code) === MEDIA_CONNECTION_ERROR_CODE) {
          // First media failure on this machine: the direct path does not work
          // here, so pin every later call to TURN relay over TCP rather than
          // letting the user rediscover this on every attempt. If we are ALREADY
          // on relay then TURN itself could not be reached, which is a different
          // and much worse problem, so say so plainly instead of looping.
          const wasAlreadyOnRelay = forceTurnRelayRef.current;

          if (!wasAlreadyOnRelay && iceServersRef.current.length) {
            forceTurnRelayRef.current = true;
            setIsForcingTurnRelay(true);
            writeForceTurnRelay(true);
          }

          setCallStatus(
            wasAlreadyOnRelay
              ? "Call failed: could not reach Twilio's TURN relay either. The network is blocking media outright."
              : "Call failed: no direct audio path. Switched to a TURN relay over TCP, try the call again."
          );
          setCallPhase(CALL_PHASE.FAILED);

          try {
            call.disconnect();
          } catch (disconnectError) {
            console.error(
              "Failed to end call after media failure:",
              disconnectError
            );
          }

          return;
        }

        setCallStatus("Call connection lost. Reconnecting...");
      });

      call.on("reconnected", () => {
        console.log("Twilio call reconnected");
        setCallStatus("Call reconnected.");
      });
    },
    [clearIncomingUi, incomingCaller, resetLiveCallState]
  );

  const bindDeviceEvents = useCallback(
    (device) => {
      console.log("Binding Twilio device events");

      device.on("registered", () => {
        console.log("Twilio device registered as:", identity);

        reconnectAttemptsRef.current = 0;
        isRateLimitedRef.current = false;
        clearReconnectTimer();

        setCallPhase((prevPhase) =>
          prevPhase === CALL_PHASE.CONNECTED ||
          prevPhase === CALL_PHASE.INCOMING
            ? prevPhase
            : CALL_PHASE.READY
        );

        setCallStatus((prevStatus) => {
          if (
            callPhase === CALL_PHASE.CONNECTED ||
            callPhase === CALL_PHASE.INCOMING
          ) {
            return prevStatus;
          }

          return `Browser phone ready as ${identity}`;
        });
      });

      device.on("unregistered", () => {
        console.log("Twilio device unregistered");
        setCallStatus("Browser phone disconnected from Twilio.");
      });

      // The access token only lives an hour (CallOutboundController::preauth).
      // Without this the signalling socket loses its credentials and the SDK
      // starts throwing TransportError 31009 at anyone with the drawer open.
      device.on("tokenWillExpire", () => {
        console.log("Twilio token about to expire, refreshing");

        requestToken({ reason: "refresh", phone: "" });
      });

      device.on("incoming", async (call) => {
        const caller = getIncomingCaller(call);
        const meta = getIncomingMeta(call);
        const displayName = meta.customerName || meta.phone || caller;

        console.group("Incoming Twilio call");
        console.log("status:", call?.status?.());
        console.log("direction:", call?.direction);
        console.log("parameters:", call?.parameters || {});
        console.log("customParameters:", mapToObject(call?.customParameters));
        console.log("parsed meta:", meta);
        console.groupEnd();

        if (activeCallRef.current || incomingCallRef.current) {
          try {
            call.reject();
          } catch (error) {
            console.error("Failed to reject second incoming call:", error);
          }

          return;
        }

        incomingCallRef.current = call;

        setIncomingCaller(caller);
        setIncomingMeta(meta);
        setCallPhase(CALL_PHASE.INCOMING);
        setCallStatus(`Incoming call from ${displayName}`);

        bindCallEvents(call, "inbound", meta);

        if (!AUTO_ACCEPT_INBOUND_FOR_TESTING) return;

        try {
          setCallStatus(`Auto-answering call from ${displayName}...`);

          await call.accept();

          activeCallRef.current = call;
          incomingCallRef.current = null;

          setCallPhase(CALL_PHASE.CONNECTED);
          connectedAtRef.current = Date.now();
          setConnectedAt(Date.now());
          setDurationSeconds(0);
          setCallStatus(`Inbound call connected with ${displayName}`);
        } catch (error) {
          console.error("Failed to auto-accept incoming call:", error);

          incomingCallRef.current = null;
          setLastError(error);
          setCallPhase(CALL_PHASE.FAILED);
          setCallStatus(
            getErrorMessage(error, "Failed to auto-accept incoming call")
          );
        }
      });

      device.on("error", (error, call) => {
        // While rate-limited every message on the dead socket raises 31009.
        // Logging each one buries the actual cause in hundreds of lines, so
        // swallow the storm before it reaches the console.
        if (
          isRateLimitedRef.current &&
          isRecoverableSignallingError(error)
        ) {
          return;
        }

        console.error("Twilio device error:", {
          error,
          callStatus: call?.status?.(),
        });

        setLastError(error);

        // A second dial while a call is up is our bug, not a call failure —
        // the existing call is still connected, so leave it alone.
        if (isCallAlreadyActiveError(error)) {
          syncDeviceBusy();
          setCallStatus("A call is already in progress.");
          return;
        }

        if (
          Number(error?.code) === 31005 ||
          String(error?.message || "").includes("13214")
        ) {
          setCallPhase(CALL_PHASE.FAILED);
          setCallStatus(
            "Call failed: backend must provide a valid Twilio caller ID."
          );
          return;
        }

        // Rate limited. Twilio closes the socket straight after this, so every
        // later message raises 31009 — reconnecting now would just re-trip it.
        // Suppress the 31009 storm and come back once, well after the limit.
        if (Number(error?.code) === RATE_LIMITED_ERROR_CODE) {
          isRateLimitedRef.current = true;
          clearReconnectTimer();

          setCallPhase(CALL_PHASE.FAILED);
          setCallStatus(
            "Twilio rate limit hit. Waiting a minute before reconnecting."
          );

          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            isRateLimitedRef.current = false;
            reconnectAttemptsRef.current = 0;

            requestToken({ reason: "refresh", phone: "" });
          }, RATE_LIMIT_BACKOFF_MS);

          return;
        }

        // Everything on a dead socket reports 31009. While the rate-limit
        // backoff is running, that is expected noise, not new information.
        if (isRateLimitedRef.current) return;

        // A dropped signalling socket is not the user's problem — it happens on
        // token expiry, sleep and wifi changes. Recover quietly instead of
        // dumping a raw Twilio code into the status panel of whatever page they
        // happen to be on. Ask the device too: our refs can drift out of step
        // with a call that is genuinely still up.
        const hasLiveCall =
          !!activeCallRef.current ||
          !!incomingCallRef.current ||
          getDeviceIsBusy(deviceRef.current);

        if (isRecoverableSignallingError(error) && !hasLiveCall) {
          const willRetry = scheduleReconnect({
            immediate: reconnectAttemptsRef.current === 0,
          });

          setCallPhase(
            willRetry ? CALL_PHASE.REGISTERING : CALL_PHASE.FAILED
          );
          setCallStatus(
            willRetry
              ? "Browser phone lost its connection. Reconnecting..."
              : "Browser phone could not reconnect. Reload the page to retry."
          );
          return;
        }

        setCallPhase(CALL_PHASE.FAILED);

        if (isRecoverableSignallingError(error)) {
          setCallStatus("Call dropped: lost connection to Twilio.");
          return;
        }

        setCallStatus(getErrorMessage(error, "Twilio device error"));
      });
    },
    [
      bindCallEvents,
      callPhase,
      clearReconnectTimer,
      identity,
      requestToken,
      scheduleReconnect,
      syncDeviceBusy,
    ]
  );

  const ensureDeviceRegistered = useCallback(
    async (token) => {
      if (!token) {
        setCallPhase(CALL_PHASE.FAILED);
        setCallStatus("Token response received, but no Token was found.");
        return null;
      }

      // A destroyed device cannot be revived — drop it so a fresh one is built.
      if (deviceRef.current?.state === "destroyed") {
        deviceRef.current = null;
      }

      if (deviceRef.current) {
        return deviceRef.current;
      }

      setCallPhase(CALL_PHASE.REGISTERING);
      setCallStatus("Registering browser phone...");

      const device = new Device(token, {
        edge: "dublin",
        logLevel: 1,
        tokenRefreshMs: TOKEN_REFRESH_MS,
      });

      deviceRef.current = device;
      bindDeviceEvents(device);
      // The AudioHelper is created with the Device, so this is the earliest
      // point the device lists exist and the saved speaker choice can be applied.
      bindAudioDevice(device);

      setCallStatus("Requesting microphone / registering phone...");
      await device.register();

      console.log("Twilio device register completed:", {
        identity,
      });

      return device;
    },
    [bindAudioDevice, bindDeviceEvents, identity]
  );

  const registerDevice = useCallback(() => {
    if (!canUseTwilio || !identity) return;
    if (deviceRef.current) return;
    if (hasRequestedRegisterTokenRef.current) return;

    hasRequestedRegisterTokenRef.current = true;

    setCallPhase(CALL_PHASE.GETTING_TOKEN);
    setCallStatus("Getting token to register browser phone...");

    requestToken({
      reason: "register",
      phone: "",
    });
  }, [canUseTwilio, identity, requestToken]);

  const startOutboundCall = useCallback(
    ({ phone, person = null } = {}) => {
      const normalisedPhone = normalisePhone(phone);

      if (!canUseTwilio) {
        setCallPhase(CALL_PHASE.FAILED);
        setCallStatus("Calling is not enabled for this user.");
        return;
      }

      if (!normalisedPhone) {
        setCallPhase(CALL_PHASE.FAILED);
        setCallStatus("Please enter a phone number");
        return;
      }

      // Twilio has closed our socket for rate limiting. Dialling now cannot
      // reach it, so say why rather than letting the attempt fail obscurely.
      if (isRateLimitedRef.current) {
        setCallStatus(
          "Twilio rate limit hit. Waiting to reconnect before you can dial."
        );
        return;
      }

      // A denied microphone cannot be recovered from in code: once the user has
      // blocked it the browser will not prompt again, only a site-settings
      // change clears it. Say that, rather than letting the SDK fail the call
      // with an opaque 31401 halfway through the dial.
      if (micPermission === MIC_PERMISSION.DENIED) {
        setCallPhase(CALL_PHASE.FAILED);
        setCallStatus(
          "Microphone access is blocked. Allow it in your browser's site settings for this page, then reload."
        );
        return;
      }

      // Refuse a second dial rather than letting the SDK throw "A Call is
      // already active" halfway through the token round trip. The in-flight ref
      // covers the window before the call object exists, which is where a
      // double-fired dial (Enter keydown plus the button's synthesised click)
      // used to slip through.
      if (
        isDiallingRef.current ||
        activeCallRef.current ||
        incomingCallRef.current ||
        getDeviceIsBusy(deviceRef.current)
      ) {
        syncDeviceBusy();
        setCallStatus("A call is already in progress. End it before dialling.");
        return;
      }

      const outboundMeta = buildOutboundMeta({
        phone: normalisedPhone,
        person,
      });

      isDiallingRef.current = true;
      pendingOutboundPhoneRef.current = normalisedPhone;
      pendingOutboundMetaRef.current = outboundMeta;

      setIncomingCaller(normalisedPhone);
      setIncomingMeta(outboundMeta);
      setLastError(null);
      setCallPhase(CALL_PHASE.GETTING_TOKEN);
      setCallStatus("Getting secure call token...");

      requestToken({
        reason: "outbound",
        phone: normalisedPhone,
      });
    },
    [canUseTwilio, micPermission, requestToken, syncDeviceBusy]
  );

  const acceptIncomingCall = useCallback(
    async ({ openCustomer = false, onOpenCustomer } = {}) => {
      const incomingCall = incomingCallRef.current;

      if (!incomingCall) {
        setCallStatus("No incoming call to accept.");
        return;
      }

      const label =
        incomingMeta?.customerName ||
        incomingMeta?.phone ||
        incomingCaller ||
        "caller";

      try {
        setCallStatus(`Accepting incoming call from ${label}...`);

        await applyInputPreference();


        await incomingCall.accept({
          rtcConfiguration: buildRtcConfiguration(),
        });

        activeCallRef.current = incomingCall;
        incomingCallRef.current = null;

        setCallPhase(CALL_PHASE.CONNECTED);
        connectedAtRef.current = Date.now();
        setConnectedAt(Date.now());
        setDurationSeconds(0);
        setIsMuted(false);
        setIsOnHold(false);
        setCallStatus(`Inbound call connected with ${label}`);

        if (openCustomer && typeof onOpenCustomer === "function") {
          onOpenCustomer(incomingMeta);
        }
      } catch (error) {
        console.error("Failed to accept incoming call:", error);

        incomingCallRef.current = null;
        setLastError(error);
        setCallPhase(CALL_PHASE.FAILED);
        setCallStatus(getErrorMessage(error, "Failed to accept incoming call"));
      }
    },
    [applyInputPreference, buildRtcConfiguration, incomingCaller, incomingMeta]
  );

  const rejectIncomingCall = useCallback(() => {
    const incomingCall = incomingCallRef.current;

    if (!incomingCall) {
      setCallStatus("No incoming call to reject.");
      return;
    }

    try {
      incomingCall.reject();
    } catch (error) {
      console.error("Failed to reject incoming call:", error);
    }

    incomingCallRef.current = null;
    clearIncomingUi();
    resetLiveCallState();

    setCallPhase(CALL_PHASE.READY);
    setCallStatus("Incoming call rejected. Browser phone ready.");
  }, [clearIncomingUi, resetLiveCallState]);

  const endCall = useCallback(() => {
    cleanupActiveCalls();

    // Our refs can lose a call the SDK is still holding (a rejected second dial
    // used to do exactly that), so hang up anything the device still has.
    if (deviceRef.current && getDeviceIsBusy(deviceRef.current)) {
      try {
        deviceRef.current.disconnectAll();
      } catch (error) {
        console.error("Failed to disconnect all Twilio calls:", error);
      }
    }

    pendingOutboundPhoneRef.current = "";
    pendingOutboundMetaRef.current = {};

    clearIncomingUi();
    resetLiveCallState();

    if (deviceRef.current) {
      setCallPhase(CALL_PHASE.READY);
      setCallStatus("Call ended. Browser phone ready.");
      return;
    }

    setCallPhase(CALL_PHASE.ENDED);
    setCallStatus("Call ended");
  }, [cleanupActiveCalls, clearIncomingUi, resetLiveCallState]);

  const toggleMute = useCallback(() => {
    const activeCall = activeCallRef.current;

    if (!activeCall) {
      setCallStatus("No active call to mute.");
      return;
    }

    setIsMuted((prev) => {
      const nextMuted = !prev;

      try {
        activeCall.mute(nextMuted);
        setCallStatus(nextMuted ? "Call muted." : "Call unmuted.");
      } catch (error) {
        console.error("Failed to toggle mute:", error);
        setCallStatus("Could not toggle mute.");
        return prev;
      }

      return nextMuted;
    });
  }, []);

  const holdCall = useCallback(() => {
    setIsOnHold((prev) => {
      const next = !prev;

      setCallStatus(
        next
          ? "Hold is UI-only for now. Backend conference hold will be wired later."
          : "Call resumed. Backend conference resume will be wired later."
      );

      return next;
    });
  }, []);

  const transferCall = useCallback(() => {
    setCallStatus("Transfer is coming soon.");
  }, []);

  const passCall = useCallback(() => {
    setCallStatus("Pass call is coming soon.");
  }, []);

  useEffect(() => {
    registerDevice();
  }, [registerDevice]);

  useEffect(() => {
    if (!connectedAt) return undefined;

    const timer = setInterval(() => {
      setDurationSeconds(Math.floor((Date.now() - connectedAt) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [connectedAt]);

  useEffect(() => {
    if (!preauthData) return;

    const token = getTokenFromResponse(preauthData);

    // Keep these even when the token itself is a duplicate: NTS credentials are
    // reissued on every mint and the previous set eventually expires.
    const iceServers = getIceServersFromResponse(preauthData);

    if (iceServers.length) {
      iceServersRef.current = iceServers;
    } else {
      console.warn(
        "Twilio token response carried no IceServers. Media will have no STUN or TURN path and calls will ring but stay silent."
      );
    }

    if (!token) {
      setCallPhase(CALL_PHASE.FAILED);
      setCallStatus("Token response received, but no Token was found.");
      return;
    }

    if (lastTokenRef.current === token) return;

    lastTokenRef.current = token;

    // Refresh path: hand the new token to the device we already have rather
    // than building a second one, then make sure it is still registered.
    if (isRefreshingTokenRef.current) {
      isRefreshingTokenRef.current = false;

      const device = deviceRef.current;

      if (device && device.state !== "destroyed") {
        try {
          device.updateToken(token);

          if (device.state === "unregistered") {
            device.register().catch((error) => {
              console.error("Failed to re-register Twilio device:", error);
              scheduleReconnect();
            });
          } else {
            reconnectAttemptsRef.current = 0;
            clearReconnectTimer();

            setCallPhase((prevPhase) =>
              prevPhase === CALL_PHASE.REGISTERING ||
              prevPhase === CALL_PHASE.FAILED
                ? CALL_PHASE.READY
                : prevPhase
            );
            setCallStatus((prevStatus) =>
              String(prevStatus).toLowerCase().includes("reconnect")
                ? `Browser phone ready as ${identity}`
                : prevStatus
            );
          }

          console.log("Twilio token refreshed for:", identity);
          return;
        } catch (error) {
          console.error("Failed to update Twilio token:", error);
        }
      }

      // No usable device left — fall through and build one from this token.
      deviceRef.current = null;
      hasRequestedRegisterTokenRef.current = true;
    }

    const pendingPhone = pendingOutboundPhoneRef.current;

    if (pendingPhone) {
      const outboundMeta = pendingOutboundMetaRef.current || {};

      // Consume the pending dial NOW. Leaving it set meant every later token
      // response (a refresh, a re-register) re-entered this branch and dialled
      // the same number again, which the SDK rejects with "A Call is already
      // active" while the first call is still ringing the recipient.
      pendingOutboundPhoneRef.current = "";
      pendingOutboundMetaRef.current = {};

      const startOutbound = async () => {
        try {
          const device = await ensureDeviceRegistered(token);

          if (!device) {
            isDiallingRef.current = false;
            return;
          }

          if (getDeviceIsBusy(device)) {
            console.warn("Ignoring outbound dial, device already on a call");
            isDiallingRef.current = false;
            syncDeviceBusy();
            return;
          }

          setCallPhase(CALL_PHASE.DIALLING);
          setCallStatus(`Dialling ${pendingPhone}...`);

          // Pin the chosen microphone before the offer is built. The SDK falls
          // back to the system default otherwise, so a saved choice has to be
          // re-applied on every call rather than set once.
          await applyInputPreference();

          const call = await device.connect({
            params: {
              To: pendingPhone,
            },
            // rtcConfiguration is a CONNECT option, not a Device option - the
            // SDK reads it from here (device.js) and from Call.AcceptOptions,
            // and ignores anything passed to the Device constructor.
            rtcConfiguration: buildRtcConfiguration(),
          });

          activeCallRef.current = call;
          isDiallingRef.current = false;
          setIsDeviceBusy(true);
          bindCallEvents(call, "outbound", outboundMeta);
        } catch (error) {
          console.error("Failed to start outbound Twilio call:", error);

          isDiallingRef.current = false;

          // "A Call is already active" means the existing call is fine and our
          // dial was the mistake. Clearing activeCallRef here used to orphan a
          // live, ringing call: the drawer lost it and End went disabled.
          if (isCallAlreadyActiveError(error) || getDeviceIsBusy(deviceRef.current)) {
            syncDeviceBusy();
            return;
          }

          activeCallRef.current = null;
          setIsDeviceBusy(false);
          setLastError(error);
          setCallPhase(CALL_PHASE.FAILED);
          setCallStatus(getErrorMessage(error, "Failed to start call"));
        }
      };

      startOutbound();
      return;
    }

    ensureDeviceRegistered(token).catch((error) => {
      console.error("Failed to register Twilio device:", error);

      hasRequestedRegisterTokenRef.current = false;
      setLastError(error);
      setCallPhase(CALL_PHASE.FAILED);
      setCallStatus(getErrorMessage(error, "Failed to register browser phone"));
    });
  }, [
    applyInputPreference,
    bindCallEvents,
    buildRtcConfiguration,
    clearReconnectTimer,
    ensureDeviceRegistered,
    identity,
    preauthData,
    scheduleReconnect,
    syncDeviceBusy,
  ]);

  useEffect(() => {
    if (!tokenError) return;

    const wasRefreshing = isRefreshingTokenRef.current;

    isRefreshingTokenRef.current = false;
    hasRequestedRegisterTokenRef.current = false;
    setLastError(tokenError);

    // A failed refresh should keep trying rather than leaving a dead phone.
    if (wasRefreshing && scheduleReconnect()) {
      setCallPhase(CALL_PHASE.REGISTERING);
      setCallStatus("Browser phone lost its connection. Reconnecting...");
      return;
    }

    setCallPhase(CALL_PHASE.FAILED);
    setCallStatus(getErrorMessage(tokenError, "Could not get Twilio token"));
  }, [scheduleReconnect, tokenError]);

  useEffect(() => {
    return () => {
      destroyDevice();
    };
  }, [destroyDevice]);

  const value = useMemo(
    () => ({
      CALL_PHASE,
      identity,
      canUseTwilio,
      callPhase,
      callStatus,
      tokenLoading,
      incomingCaller,
      incomingMeta,
      lastError,
      isRegistered: !!deviceRef.current,
      isDeviceBusy,
      hasIncomingCall: callPhase === CALL_PHASE.INCOMING,
      hasConnectedCall: callPhase === CALL_PHASE.CONNECTED,
      isMuted,
      isOnHold,
      connectedAt,
      durationSeconds,
      startOutboundCall,
      acceptIncomingCall,
      rejectIncomingCall,
      endCall,
      toggleMute,
      holdCall,
      transferCall,
      passCall,
      isForcingTurnRelay,
      setForceTurnRelay,
      // Microphone permission plus mic/speaker/ringtone selection.
      ...audioDevices,
    }),
    [
      acceptIncomingCall,
      callPhase,
      callStatus,
      canUseTwilio,
      connectedAt,
      durationSeconds,
      endCall,
      holdCall,
      identity,
      incomingCaller,
      incomingMeta,
      isDeviceBusy,
      isMuted,
      isOnHold,
      lastError,
      passCall,
      isForcingTurnRelay,
      setForceTurnRelay,
      audioDevices,
      rejectIncomingCall,
      startOutboundCall,
      tokenLoading,
      toggleMute,
      transferCall,
    ]
  );

  return (
    <TwilioCallContext.Provider value={value}>
      {children}
    </TwilioCallContext.Provider>
  );
};

export const useTwilioCall = () => {
  const context = useContext(TwilioCallContext);

  if (!context) {
    throw new Error("useTwilioCall must be used inside TwilioCallProvider");
  }

  return context;
};

export { CALL_PHASE, normalisePhone };
